import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Crown, Shield, Edit3, Eye } from 'lucide-react';

const ROLE_CONFIG = {
  owner: {
    label: 'Owner',
    description: 'Full control, can delete project',
    icon: Crown,
    color: 'text-amber-500',
    badgeClass: 'bg-amber-500/10 text-amber-600 border-amber-200',
  },
  admin: {
    label: 'Admin',
    description: 'Can invite members, manage settings',
    icon: Shield,
    color: 'text-blue-500',
    badgeClass: 'bg-blue-500/10 text-blue-600 border-blue-200',
  },
  editor: {
    label: 'Editor',
    description: 'Can edit content and sources',
    icon: Edit3,
    color: 'text-green-500',
    badgeClass: 'bg-green-500/10 text-green-600 border-green-200',
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access',
    icon: Eye,
    color: 'text-gray-500',
    badgeClass: 'bg-gray-500/10 text-gray-600 border-gray-200',
  },
};

export function RoleBadge({ role, size = 'default' }) {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.viewer;
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={`${config.badgeClass} ${size === 'sm' ? 'text-xs px-1.5 py-0' : ''}`}
    >
      <Icon className={`h-3 w-3 mr-1 ${config.color}`} />
      {config.label}
    </Badge>
  );
}

export default function RoleSelector({
  value,
  onChange,
  disabled = false,
  excludeOwner = false,
  maxRole = null,
}) {
  const ROLE_HIERARCHY = { owner: 4, admin: 3, editor: 2, viewer: 1 };

  const availableRoles = Object.entries(ROLE_CONFIG).filter(([roleKey]) => {
    if (excludeOwner && roleKey === 'owner') return false;
    if (maxRole && ROLE_HIERARCHY[roleKey] > ROLE_HIERARCHY[maxRole]) return false;
    return true;
  });

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-[140px]">
        <SelectValue placeholder="Select role">
          {value && ROLE_CONFIG[value] && (
            <span className="flex items-center gap-2">
              {React.createElement(ROLE_CONFIG[value].icon, {
                className: `h-4 w-4 ${ROLE_CONFIG[value].color}`,
              })}
              {ROLE_CONFIG[value].label}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {availableRoles.map(([roleKey, config]) => {
          const Icon = config.icon;
          return (
            <SelectItem key={roleKey} value={roleKey}>
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${config.color}`} />
                <div>
                  <div className="font-medium">{config.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {config.description}
                  </div>
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
